import React, { isValidElement, type ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinkPreview from 'components/LinkPreview';

const MARKDOWN_TABLE_CELL_MAX_WIDTH_PX = 260;

const getPlainTextFromChildren = (children: ReactNode): string => {
  if (children == null || typeof children === 'boolean') {
    return '';
  }

  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(getPlainTextFromChildren).join('');
  }

  if (isValidElement<{ children?: ReactNode }>(children)) {
    return getPlainTextFromChildren(children.props.children);
  }

  return '';
};

/**
 * Creates ReactMarkdown components with Simplifi typography system
 * Optimized for chat bubbles with compact spacing
 * @param theme - MUI theme object
 * @returns Components object for ReactMarkdown
 */
export const createMarkdownComponents = (theme: Theme) => {
  const tableCellSx = {
    display: 'table-cell',
    padding: theme.spacing(1, 1.5),
    textAlign: 'left' as const,
    verticalAlign: 'top' as const,
    whiteSpace: 'nowrap' as const,
    minWidth: theme.spacing(12),
    maxWidth: MARKDOWN_TABLE_CELL_MAX_WIDTH_PX,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return {
    p: ({ children }: any) => (
      <Typography
        variant='paragraphRegular'
        component='p'
        sx={{ marginBottom: theme.spacing(0.5), '&:last-child': { marginBottom: 0 } }}
      >
        {children}
      </Typography>
    ),
    h1: ({ children }: any) => (
      <Typography
        variant='titleBold'
        component='h1'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(1) }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }: any) => (
      <Typography
        variant='titleMedium'
        component='h2'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(1) }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }: any) => (
      <Typography
        variant='bodyBold'
        component='h3'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.75) }}
      >
        {children}
      </Typography>
    ),
    h4: ({ children }: any) => (
      <Typography
        variant='bodyMedium'
        component='h4'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.75) }}
      >
        {children}
      </Typography>
    ),
    h5: ({ children }: any) => (
      <Typography
        variant='paragraphBold'
        component='h5'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}
      >
        {children}
      </Typography>
    ),
    h6: ({ children }: any) => (
      <Typography
        variant='paragraphMedium'
        component='h6'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}
      >
        {children}
      </Typography>
    ),
    strong: ({ children }: any) => (
      <Typography variant='inherit' component='strong' sx={{ display: 'inline', fontWeight: '500' }}>
        {children}
      </Typography>
    ),
    em: ({ children }: any) => (
      <Typography
        variant='paragraphRegular'
        component='em'
        sx={{ display: 'inline', fontStyle: 'italic', fontWeight: 'inherit' }}
      >
        {children}
      </Typography>
    ),
    code: ({ children }: any) => (
      <Typography
        variant='captionRegular'
        component='code'
        sx={{
          display: 'inline',
          fontFamily: 'monospace',
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(0.5),
          borderRadius: theme.shape.borderRadius,
        }}
      >
        {children}
      </Typography>
    ),
    pre: ({ children }: any) => {
      // Extract text content from code element if needed
      // ReactMarkdown passes code element as children
      return (
        <Box
          component='pre'
          sx={{
            color: theme.palette.text.contrastText,
            width: '100%',
            backgroundColor: theme.palette.backdrop.dark,
            padding: theme.spacing(0.75),
            borderRadius: theme.shape.borderRadius * 0.25,
            overflow: 'auto',
            marginBottom: theme.spacing(0.5),
            marginTop: theme.spacing(0.5),
            ...theme.typography.captionRegular,
            fontFamily: 'monospace',
            whiteSpace: 'pre',
            '& code': {
              display: 'block',
              backgroundColor: 'transparent',
              padding: 0,
              borderRadius: 0,
              whiteSpace: 'pre',
            },
          }}
        >
          {children}
        </Box>
      );
    },
    ul: ({ children }: any) => (
      <Box
        component='ul'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5), paddingLeft: theme.spacing(2.5) }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }: any) => (
      <Box
        component='ol'
        sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5), paddingLeft: theme.spacing(2.5) }}
      >
        {children}
      </Box>
    ),
    li: ({ children }: any) => (
      <Typography variant='paragraphRegular' component='li' sx={{ marginBottom: theme.spacing(1) }}>
        {children}
      </Typography>
    ),
    blockquote: ({ children }: any) => (
      <Box
        component='blockquote'
        sx={{
          borderLeft: `3px solid ${theme.palette.divider}`,
          paddingLeft: theme.spacing(1.5),
          marginLeft: 0,
          marginRight: 0,
          marginTop: theme.spacing(0.5),
          marginBottom: theme.spacing(0.5),
          fontStyle: 'italic',
        }}
      >
        <Typography variant='paragraphRegular'>{children}</Typography>
      </Box>
    ),
    a: ({ children, href }: any) => (
      <LinkPreview href={href} showFavicon={false}>
        {children}
      </LinkPreview>
    ),
    hr: () => (
      <Box
        component='hr'
        sx={{
          border: 'none',
          borderTop: `1px solid ${theme.palette.divider}`,
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),
          width: '100%',
        }}
      />
    ),
    table: ({ children }: any) => (
      <Box
        sx={{
          display: 'inline-block',
          width: 'auto',
          maxWidth: '100%',
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: theme.spacing(0.5),
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.border.light}`,
          verticalAlign: 'top',
        }}
      >
        <Box
          component='table'
          sx={{
            width: 'max-content',
            borderCollapse: 'collapse',
            tableLayout: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }: any) => <Box component='thead'>{children}</Box>,
    tbody: ({ children }: any) => <Box component='tbody'>{children}</Box>,
    tr: ({ children }: any) => (
      <Box
        component='tr'
        sx={{
          display: 'table-row',
          borderBottom: `1px solid ${theme.palette.border.light}`,
          '&:last-child': {
            borderBottom: 'none',
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {children}
      </Box>
    ),
    th: ({ children }: any) => {
      const title = getPlainTextFromChildren(children).trim() || undefined;

      return (
        <Typography
          variant='paragraphMedium'
          component='th'
          title={title}
          sx={{
            ...tableCellSx,
            borderBottom: `1px solid ${theme.palette.border.main}`,
            backgroundColor: theme.palette.background.default,
            fontWeight: 500,
          }}
        >
          {children}
        </Typography>
      );
    },
    td: ({ children }: any) => {
      const title = getPlainTextFromChildren(children).trim() || undefined;

      return (
        <Typography variant='paragraphRegular' component='td' title={title} sx={tableCellSx}>
          {children}
        </Typography>
      );
    },
  };
};
